import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MarketingLayout } from '@byscott-io/corebyscott';

const FEATURES = [
  {
    icon: '📖',
    title: 'Recipe Management',
    description: 'Store all your recipes in one place with detailed information.',
    details: [
      'Add title, description, and step-by-step instructions',
      'Track ingredients with quantities and units',
      'Categorize recipes (breakfast, dinner, dessert, etc.)',
      'Add prep time, cook time, and servings',
      'Import recipes from URLs or scan recipe cards',
      'Add personal notes and variations',
    ],
  },
  {
    icon: '📅',
    title: 'Meal Planning',
    description: 'Plan your meals ahead of time with our calendar view.',
    details: [
      'Drag and drop recipes onto dates',
      'Plan by meal type (breakfast, lunch, dinner, snack)',
      'See what you had recently to avoid repetition',
      'Add notes for extras like side dishes',
      'View weekly or monthly calendars',
    ],
  },
  {
    icon: '🛒',
    title: 'Smart Shopping Lists',
    description: 'Generate shopping lists automatically from your meal plans.',
    details: [
      'Select recipes and generate a shopping list',
      'Scale recipes by adjusting servings',
      'Consolidate duplicate ingredients',
      'Check off items as you shop',
      'Add manual items not from recipes',
    ],
  },
  {
    icon: '👨‍👩‍👧‍👦',
    title: 'Family Sharing',
    description: 'Share your kitchen with family members.',
    details: [
      'Invite family members by email',
      'Everyone can add and edit recipes',
      'Collaborative meal planning',
      'Shared shopping lists',
      'Role-based permissions (owner, admin, member)',
    ],
  },
  {
    icon: '🌐',
    title: 'Public Recipes',
    description: 'Share recipes with the community or discover new favorites.',
    details: [
      'Make any recipe public to share',
      'Browse recipes from other users',
      'Search and filter by category',
      'No account needed to browse',
    ],
  },
  {
    icon: '🎲',
    title: 'Feeling Lucky',
    description: "Can't decide what to make? Let us help.",
    details: [
      'Get a random recipe suggestion',
      'Weighted toward recipes you haven\'t made recently',
      'Discover forgotten favorites',
      'Break out of meal ruts',
    ],
  },
];

export default function FeaturesPage() {
  return (
    <MarketingLayout>
      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Your Family's Recipe Collection,
            <span className="text-indigo-600"> Organized</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
            Store recipes, plan meals, and generate shopping lists - all in one place.
            Share your kitchen with family members.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="px-8 py-3 bg-indigo-600 text-white rounded-lg text-lg font-medium hover:bg-indigo-700 transition-colors"
            >
              Get Started Free
            </Link>
            <Link
              to="/"
              className="px-8 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Browse Recipes
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-12 px-4 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-10">
            Everything You Need
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to organize your recipes?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Start for free. No credit card required.
          </p>
          <Link
            to="/register"
            className="px-8 py-3 bg-indigo-600 text-white rounded-lg text-lg font-medium hover:bg-indigo-700 transition-colors"
          >
            Create Your Kitchen
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

function FeatureCard({ icon, title, description, details }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="flex items-start justify-between">
        <div className="text-4xl mb-3">{icon}</div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400">{description}</p>

      {/* Expandable details */}
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? 'max-h-96 mt-4 pt-4 border-t border-gray-200 dark:border-gray-600' : 'max-h-0'
        }`}
      >
        <ul className="space-y-2">
          {details.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <span className="text-green-500 mt-0.5">✓</span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
